// AdminWallet.js
import React, { useState, useEffect } from "react";
import { Table, Input, Button, Drawer, Tag, Tooltip, message, Modal, Tabs } from "antd";
import {
  SearchOutlined, ReloadOutlined, EyeOutlined, WalletOutlined,
  CheckCircleOutlined, UserOutlined, ArrowUpOutlined, ArrowDownOutlined,
  DeleteOutlined, ShoppingCartOutlined, DownOutlined, UpOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { BASE_API } from "../../../../Api/Api";
import { UserGet, UserDataGet, adminGetWalletPurchases, adminDeleteWalletPurchase } from "../../../../Api/CoreApi";
import "./AdminWallet.css";

const AdminWallet = () => {
  const [activeTab, setActiveTab] = useState("wallets");
  const [wallets, setWallets] = useState([]);
  const [walletPurchases, setWalletPurchases] = useState([]);

  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const [userTransactions, setUserTransactions] = useState([]);
  const [txLoading, setTxLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // 📱 Mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showStats, setShowStats] = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` },
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [walletRes, userRes, profileRes, purchasesRes] = await Promise.all([
        axios.get(`${BASE_API}/api/admin/wallet/`, getAuthHeaders()),
        UserGet(),
        UserDataGet(),
        adminGetWalletPurchases(),
      ]);

      const allUsers = Array.isArray(userRes) ? userRes : [];
      const allProfiles = Array.isArray(profileRes) ? profileRes : [];
      const allWallets = Array.isArray(walletRes.data) ? walletRes.data : [];
      const rawPurchases = Array.isArray(purchasesRes) ? purchasesRes : [];

      const mergedWallets = allWallets.map((wallet) => {
        const user = allUsers.find((u) => String(u.id) === String(wallet.user_id));
        const profile = allProfiles.find((p) => String(p.user_id) === String(wallet.user_id));

        return {
          ...wallet,
          id: wallet.user_id,
          user_id: wallet.user_id,
          wallet_id: wallet.id,
          username: wallet.username || user?.username || "N/A",
          email: wallet.email || user?.email || profile?.email || "No email",
          first_name: profile?.firstname || user?.first_name || "",
          contact: profile?.contact || user?.contact || "",
          pic: profile?.pic || user?.pic || null,
          balance: Number(wallet.balance || 0),
          total_earned: Number(wallet.total_earned || 0),
          total_spent: Number(wallet.total_spent || 0),
          real_profile_id: wallet.profile_id || user?.profile_id || profile?.profile_id || "N/A",
          is_active: user?.is_active ?? true,
          is_premium: user?.premium || profile?.premium || false,
          date_joined: user?.date_joined || wallet.created_at,
        };
      });

      const mergedPurchases = rawPurchases.map((purchase) => {
        const user = allUsers.find((u) => String(u.id) === String(purchase.user_id));
        const profile = allProfiles.find((p) => String(p.user_id) === String(purchase.user_id));
        return {
          ...purchase,
          username: purchase.username || user?.username || "N/A",
          real_profile_id: purchase.real_profile_id || user?.profile_id || profile?.profile_id || "N/A",
          first_name: purchase.first_name || profile?.firstname || user?.first_name || "",
          pic: purchase.pic || profile?.pic || user?.pic || null,
        };
      });

      setWallets(mergedWallets.sort((a, b) => (b.wallet_id || 0) - (a.wallet_id || 0)));
      setWalletPurchases(mergedPurchases);
    } catch (err) {
      console.error(err);
      message.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const fetchUserTransactions = async (userId) => {
    setTxLoading(true);
    try {
      const res = await axios.get(`${BASE_API}/api/admin/wallet/${userId}/`, getAuthHeaders());
      setUserTransactions(res.data?.transactions || []);
    } catch (err) {
      message.error("Failed to fetch transactions");
    } finally {
      setTxLoading(false);
    }
  };

  const handleOpenDrawer = async (user) => {
    setSelectedUser(user);
    setDetailsDrawerOpen(true);
    await fetchUserTransactions(user.user_id);
  };

  const handleDeleteWallet = (record) => {
    Modal.confirm({
      title: "Delete Entire Wallet?",
      content: `This will permanently delete the wallet and ALL transactions for ${record.username || "this user"}.`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await axios.delete(`${BASE_API}/api/admin/wallet/${record.user_id}/?all=true`, getAuthHeaders());
          message.success("Wallet deleted successfully");
          setDetailsDrawerOpen(false);
          fetchAllData();
        } catch (err) {
          message.error("Failed to delete wallet");
        }
      },
    });
  };

  const handleDeleteTransaction = (tx) => {
    if (!selectedUser) return;
    Modal.confirm({
      title: "Delete Transaction?",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await axios.delete(
            `${BASE_API}/api/admin/wallet/${selectedUser.user_id}/?transaction_id=${tx.id}`,
            getAuthHeaders()
          );
          message.success("Transaction deleted successfully");
          await fetchUserTransactions(selectedUser.user_id);
          fetchAllData();
        } catch (err) {
          message.error("Failed to delete transaction");
        }
      },
    });
  };

  const handleDeletePurchase = (record) => {
    Modal.confirm({
      title: "Delete Purchase Record?",
      content: `Delete "${record.plan_name || "Plan"}" purchase for ${record.username || "this user"}?`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await adminDeleteWalletPurchase(record.id);
          message.success("Purchase deleted successfully");
          fetchAllData();
        } catch (err) {
          message.error(err?.response?.data?.error || "Failed to delete purchase");
        }
      },
    });
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  const capitalize = (str) => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const walletColumns = [
    {
      title: "#",
      key: "sno",
      width: 50,
      render: (_, __, i) => <span className="sno-cell">{(currentPage - 1) * pageSize + i + 1}</span>,
    },
    {
      title: "PROFILE ID",
      key: "profile_id",
      width: 140,
      render: (_, record) => <span className="ref-cell">{record.real_profile_id}</span>,
    },
    {
      title: "USER",
      key: "user",
      width: 250,
      render: (_, record) => (
        <div className="user-name-cell">
          <div className="user-avatar">
            {record.pic ? (
              <img src={record.pic} alt="user" />
            ) : (
              (record.first_name?.[0] || record.username?.[0] || "U").toUpperCase()
            )}
          </div>
          <div className="user-name-info">
            <span className="user-name-text">{capitalize(record.first_name) || record.username || "N/A"}</span>
            <span className="user-email-text">{record.email}</span>
          </div>
        </div>
      ),
    },
    {
      title: "BALANCE",
      dataIndex: "balance",
      key: "balance",
      width: 150,
      render: (bal) => (
        <span className="balance-highlight">
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: "#52c41a",
              display: "inline-block",
            }}
          />
          ₹{Number(bal || 0).toLocaleString("en-IN")}
        </span>
      ),
    },
    {
      title: "EARNED",
      dataIndex: "total_earned",
      key: "total_earned",
      width: 120,
      render: (amt) =>
        amt > 0 ? (
          <span className="plan-tag premium">₹{Number(amt).toLocaleString("en-IN")}</span>
        ) : (
          <span className="plan-tag free">₹0</span>
        ),
    },
    {
      title: "SPENT",
      dataIndex: "total_spent",
      key: "total_spent",
      width: 120,
      render: (amt) => (
        <span style={{ color: "#4b5563", fontWeight: 700, fontSize: "12px" }}>
          ₹{Number(amt || 0).toLocaleString("en-IN")}
        </span>
      ),
    },
    {
      title: "ACTIONS",
      key: "action",
      width: 80,
      fixed: "right",
      render: (_, record) => (
        <div className="action-btns-wrap">
          <Tooltip title="View Details & History">
            <button className="action-btn view" onClick={() => handleOpenDrawer(record)}>
              <EyeOutlined />
            </button>
          </Tooltip>
          <Tooltip title="Delete Entire Wallet">
            <button className="action-btn delete" onClick={() => handleDeleteWallet(record)}>
              <DeleteOutlined />
            </button>
          </Tooltip>
        </div>
      ),
    },
  ];

  const purchaseColumns = [
    {
      title: "#",
      key: "sno",
      width: 50,
      render: (_, __, i) => <span className="sno-cell">{(currentPage - 1) * pageSize + i + 1}</span>,
    },
    {
      title: "USER",
      key: "user",
      width: 250,
      render: (_, record) => (
        <div className="user-name-cell">
          <div className="user-avatar">
            {record.pic ? (
              <img src={record.pic} alt="user" />
            ) : (
              (record.first_name?.[0] || record.username?.[0] || "U").toUpperCase()
            )}
          </div>
          <div className="user-name-info">
            <span className="user-name-text">{capitalize(record.first_name) || record.username || "N/A"}</span>
            <span className="ref-cell" style={{ marginTop: 4, display: "inline-block", fontSize: 10, padding: "1px 6px" }}>
              {record.real_profile_id}
            </span>
          </div>
        </div>
      ),
    },
    {
      title: "PLAN PURCHASED",
      dataIndex: "plan_name",
      key: "plan_name",
      width: 250,
      render: (_, record) => {
        const planName = (record.description || record.plan_name || "")
          .replace("Plan purchased via Wallet: ", "")
          .replace("Plan Activated via Wallet: ", "");
        return <span style={{ fontWeight: 700, color: "#1a0a10" }}>💎 {planName}</span>;
      },
    },
    {
      title: "AMOUNT DEDUCTED",
      dataIndex: "amount",
      key: "amount",
      width: 150,
      render: (amt) => (
        <span style={{ color: "#ef4444", fontWeight: 800, fontSize: "14px" }}>
          -₹{Number(amt || 0).toLocaleString("en-IN")}
        </span>
      ),
    },
    {
      title: "DATE & TIME",
      key: "date",
      width: 150,
      render: (_, record) => <span className="date-cell">{formatDate(record.created_at)}</span>,
    },
    {
      title: "ACTIONS",
      key: "action",
      width: 90,
      fixed: "right",
      render: (_, record) => (
        <div className="action-btns-wrap">
          <Tooltip title="Delete Purchase">
            <button className="action-btn delete" onClick={() => handleDeletePurchase(record)}>
              <DeleteOutlined />
            </button>
          </Tooltip>
        </div>
      ),
    },
  ];

  const filteredData =
    activeTab === "wallets"
      ? wallets.filter(
          (w) =>
            w.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            w.real_profile_id?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : walletPurchases.filter(
          (p) =>
            p.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            p.real_profile_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            p.description?.toLowerCase().includes(searchTerm.toLowerCase())
        );

  const totalBalance = wallets.reduce((s, w) => s + Number(w.balance || 0), 0);
  const totalSpent = wallets.reduce((s, w) => s + Number(w.total_spent || 0), 0);
  const activeWallets = wallets.filter((w) => Number(w.balance) > 0).length;

  return (
    <div className="user-tables-page">
      <div className="header-stats-wrapper">
        <div className="page-header">
          <div className="header-left">
            <div className="title-row">
              <div className="title-icon">
                <WalletOutlined />
              </div>
              <div>
                <h2 className="page-title">Wallet & Purchases</h2>
                <p className="page-subtitle">Track wallet balances and plan purchases</p>
              </div>
            </div>
          </div>
          <div className="header-actions">
            <Tooltip title="Refresh">
              <Button
                className="icon-btn icon-mobile-btn"
                icon={<ReloadOutlined />}
                onClick={fetchAllData}
                loading={loading}
              />
            </Tooltip>
            {isMobile && (
              <Button
                className="toggle-stats-btn icon-mobile-btn"
                onClick={() => setShowStats((p) => !p)}
                icon={showStats ? <UpOutlined /> : <DownOutlined />}
              />
            )}
          </div>
        </div>

        {/* Stats — desktop always show, mobile toggle */}
        <div className={`mini-stats-bar ${!isMobile || showStats ? "show" : "hide"}`}>
          <div className="mini-stat total">
            <UserOutlined className="anticon" /> <b>{wallets.length}</b> <span>TOTAL WALLETS</span>
          </div>
          <div className="mini-divider" />
          <div className="mini-stat active">
            <CheckCircleOutlined className="anticon" /> <b>{activeWallets}</b> <span>WITH BALANCE</span>
          </div>
          <div className="mini-divider" />
          <div className="mini-stat premium">
            <WalletOutlined className="anticon" /> <b>₹{totalBalance.toLocaleString("en-IN")}</b>{" "}
            <span>TOTAL BALANCE</span>
          </div>
          <div className="mini-divider" />
          <div className="mini-stat">
            <ShoppingCartOutlined className="anticon" style={{ color: "#eb2f96" }} />{" "}
            <b>{walletPurchases.length}</b> <span>PLANS BOUGHT</span>
          </div>
          <div className="mini-divider" />
          <div className="mini-stat">
            <ArrowDownOutlined className="anticon" style={{ color: "#eb2f96" }} />{" "}
            <b>₹{totalSpent.toLocaleString("en-IN")}</b> <span>WALLET SPENT</span>
          </div>
        </div>
      </div>

      <div className="table-card">
        <div style={{ marginBottom: 16, borderBottom: "1px solid #f0f0f0" }}>
          <Tabs
            activeKey={activeTab}
            onChange={(key) => {
              setActiveTab(key);
              setCurrentPage(1);
              setSearchTerm("");
            }}
            items={[
              {
                key: "wallets",
                label: (
                  <span style={{ fontWeight: 700 }}>
                    <WalletOutlined /> User Wallets
                  </span>
                ),
              },
              {
                key: "purchases",
                label: (
                  <span style={{ fontWeight: 700 }}>
                    <ShoppingCartOutlined /> Wallet Purchases History
                  </span>
                ),
              },
            ]}
          />
        </div>

        <div className="table-filters">
          <Input
            className="search-box"
            placeholder={
              activeTab === "wallets" ? "Search by name, profile ID..." : "Search by name, plan..."
            }
            prefix={<SearchOutlined className="search-icon" />}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            allowClear
          />
        </div>

        <Table
          columns={activeTab === "wallets" ? walletColumns : purchaseColumns}
          dataSource={filteredData}
          rowKey={(record) => record.id}
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            showSizeChanger: true,
            showTotal: (total) => <span style={{ fontWeight: 600 }}>Total {total} records</span>,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            },
          }}
          scroll={{ x: 950 }}
          size="middle"
          className="premium-table"
        />
      </div>

      <Drawer
        title={
          <div className="su-view-drawer-title">
            <div className="su-view-title-icon">
              <WalletOutlined />
            </div>
            <div>
              <h3>Wallet Details</h3>
              <p>Complete Information</p>
            </div>
          </div>
        }
        placement="right"
        width={isMobile ? "100%" : 520}
        onClose={() => setDetailsDrawerOpen(false)}
        open={detailsDrawerOpen}
        className="su-view-drawer"
      >
        {selectedUser && (
          <div className="su-view-content">
            <div className="su-view-header">
              <div className="su-view-avatar">
                {selectedUser.pic ? (
                  <img src={selectedUser.pic} alt="profile" />
                ) : (
                  <span>{selectedUser.first_name?.[0] || selectedUser.username?.[0] || "U"}</span>
                )}
              </div>
              <h2 className="su-view-name">
                {capitalize(selectedUser.first_name) || selectedUser.username || "N/A"}
              </h2>
              <p className="su-view-ref">{selectedUser.real_profile_id}</p>
              <div className="su-view-tags">
                {Number(selectedUser.balance) > 0 && (
                  <Tag color="green" style={{ borderRadius: 10, fontWeight: 700, padding: "4px 12px" }}>
                    💰 Balance: ₹{Number(selectedUser.balance).toLocaleString("en-IN")}
                  </Tag>
                )}
              </div>
            </div>

            <div className="su-view-section">
              <div className="su-view-section-header">
                <div className="su-section-emoji wallet">📜</div>
                <h4>Recent Transactions ({userTransactions.length})</h4>
              </div>
              {txLoading ? (
                <div style={{ textAlign: "center", padding: 20, color: "#999" }}>Loading...</div>
              ) : userTransactions.length > 0 ? (
                <div>
                  {userTransactions.map((tx, idx) => {
                    const isCredit = tx.transaction_type === "credit";
                    return (
                      <div key={tx.id || idx} className="wallet-tx-item">
                        <div
                          className="wallet-tx-icon"
                          style={{
                            background: isCredit ? "#dcfce7" : "#fef2f2",
                            color: isCredit ? "#16a34a" : "#dc2626",
                          }}
                        >
                          {isCredit ? <ArrowDownOutlined /> : <ArrowUpOutlined />}
                        </div>
                        <div className="wallet-tx-info">
                          <p className="wallet-tx-desc">
                            {tx.description || (isCredit ? "Referral Bonus" : "Plan Purchase")}
                          </p>
                          <p className="wallet-tx-date">{formatDate(tx.created_at)}</p>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div className="wallet-tx-amt" style={{ color: isCredit ? "#16a34a" : "#dc2626" }}>
                            {isCredit ? "+" : "-"}₹{Number(tx.amount).toLocaleString("en-IN")}
                          </div>
                          <Tooltip title="Delete Transaction">
                            <button
                              className="action-btn delete"
                              onClick={() => handleDeleteTransaction(tx)}
                              style={{ width: 28, height: 28 }}
                            >
                              <DeleteOutlined />
                            </button>
                          </Tooltip>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: 20, color: "#999", fontSize: 12 }}>
                  No transactions yet
                </div>
              )}
            </div>

            <div className="su-view-section">
              <div className="su-view-section-header">
                <div className="su-section-emoji account">🔐</div>
                <h4>Account Information</h4>
              </div>
              <div className="su-view-grid">
                <div className="su-view-item">
                  <span className="su-view-label">Username</span>
                  <span className="su-view-value">{selectedUser.username || "N/A"}</span>
                </div>
                <div className="su-view-item">
                  <span className="su-view-label">Profile ID</span>
                  <span className="su-view-value mono">{selectedUser.real_profile_id || "N/A"}</span>
                </div>
                <div className="su-view-item">
                  <span className="su-view-label">Mobile</span>
                  <span className="su-view-value">{selectedUser.contact || "N/A"}</span>
                </div>
                <div className="su-view-item">
                  <span className="su-view-label">Joined</span>
                  <span className="su-view-value">{formatDate(selectedUser.date_joined)}</span>
                </div>
              </div>
            </div>

            <div style={{ marginTop: 24, padding: "0 4px" }}>
              <Button
                danger
                block
                size="large"
                icon={<DeleteOutlined />}
                onClick={() => handleDeleteWallet(selectedUser)}
                style={{ fontWeight: 700, borderRadius: 10 }}
              >
                Delete Entire Wallet & History
              </Button>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default AdminWallet;